import ClearIcon from '@mui/icons-material/Clear';
import MenuIcon from '@mui/icons-material/Menu';
import {styled} from '@mui/material/styles';
import React, {HTMLAttributes, MouseEvent, useCallback} from 'react';
import {IconButton} from '../Button/IconButton';
import {Link} from '../Link';
import {excludeComponentProps} from '../utils/excludeComponentProps';
import {HeaderAppend} from './HeaderAppend';
import {HeaderDivider} from './HeaderDivider';
import {HeaderLogo} from './HeaderLogo';
import {HeaderNavigation} from './HeaderNavigation';
import {HeaderSearch} from './HeaderSearch';
import {useHeaderState} from './HeaderStateContext';
import {HeaderTitle} from './HeaderTitle';
import {getContainerMaxWidth, IsContained} from '../helpers/getContainerMaxWidth';
import {headerCssClassNames} from './headerCssClassNames';
import clsx from 'clsx';

const StyledBrandBox = styled('div')({
    display: 'inline-flex',
    alignItems: 'center',
    flexGrow: 1
});

const StyledHeaderLink = styled(Link)({
    cursor: 'pointer',
    textDecoration: 'none',

    display: 'inline-flex',
    alignItems: 'center'
});

const StyledSkipLink = styled(Link)(({theme}) => ({
    position: 'absolute',
    display: 'inline-block',
    width: '1px',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0px, 0px, 0px, 0px)',
    '&:focus': {
        position: 'static',
        width: 'auto',
        height: 'auto',
        margin: `${theme.tokens.FuelSpacing3} ${theme.tokens.FuelSpacing4}`,
        overflow: 'visible',
        clip: 'auto',
        outline: 'none',
        boxShadow: '0 0 0 2px #ffffff, 0 0 0 4px #1b1b1b'
    },
    color: theme.tokens.FuelColorGray900
}));

const StyledContainer = styled('div', {
    ...excludeComponentProps('isContained')
})<{ isContained?: IsContained }>(({
    theme, isContained
}) => ({
    height: 'inherit',
    width: '100%',
    paddingLeft: theme.tokens.FuelSpacing4,
    paddingRight: theme.tokens.FuelSpacing2,

    display: 'flex',
    alignItems: 'center',
    maxWidth: getContainerMaxWidth(theme, isContained),

    ...isContained && {
        margin: '0 auto'
    }
}));

const StyledHeader = styled('header', excludeComponentProps('showMobileTitle'))<{showMobileTitle?: boolean}>(({
    theme, showMobileTitle
}) => {
    const drawerTop = showMobileTitle ?
        'calc(var(--header-bar-height) + var(--header-append-height))' :
        'var(--header-bar-height)';

    return {
        height: 'var(--header-bar-height)',
        backgroundColor: '#fff',
        borderBottom: `1px solid ${theme.tokens.FuelColorBorderLight}`,
        boxShadow: 'none',
        position: 'relative',
        gridArea: 'header',

        '~ .MuiDrawer-modal': {
            top: drawerTop,

            '.MuiDrawer-modal, .MuiBackdrop-root, .MuiDrawer-paper': {
                top: drawerTop
            }
        }
    };
});

const StyledIconButton = styled(IconButton, {
    shouldForwardProp: (prop) => !['leftSide'].includes(String(prop))
})<{leftSide?: boolean}>(({
    theme, leftSide
}) => leftSide && {
    mr: theme.tokens.FuelSpacing2,
    ml: `-${theme.tokens.FuelSpacing2}`
});

export const HeaderBar = React.forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({
    className, children
}, ref) => {
    const state = useHeaderState();

    const hasItems = Array.isArray(state.navigationItems) && state.navigationItems.length > 0;
    const showDesktopNavigation = !state.hideNavigation && hasItems;
    const showMobileMenu = !state.hideMobileMenu && hasItems && (state.isSmallMobile || state.isMobile || state.isTablet);
    const showLeftMobileMenuButton = showMobileMenu && state.mobileMenuPosition === 'left';
    const showRightMobileMenuButton = showMobileMenu && state.mobileMenuPosition === 'right';
    const mobileBreakpoint = state.isSmallMobile || state.isMobile;
    const showMobileTitle = mobileBreakpoint && !state.hideMobileTitle;

    const closeText = state.HeaderMenuButtonProps?.closeText || 'Close Menu';
    const openText = state.HeaderMenuButtonProps?.openText || 'Open Menu';

    const renderMobileIcon = useCallback((leftSide?: boolean) => {
        return (
            <StyledIconButton
                {...state.HeaderMenuButtonProps}
                aria-expanded={state.isMobileMenuOpen}
                aria-label={state.isMobileMenuOpen ? closeText : openText}
                className={headerCssClassNames.mobileMenuButton}
                leftSide={leftSide}
                onClick={(clickEvent: MouseEvent<HTMLButtonElement>): void => {
                    state.handleMobileMenuClick(clickEvent);

                    state.isMobileMenuOpen ?
                        state.handleMobileMenuClose(clickEvent) :
                        state.handleMobileMenuOpen(clickEvent);
                }}
            >
                {state.isMobileMenuOpen ? <ClearIcon/> : <MenuIcon/>}
            </StyledIconButton>
        );
    }, [state.isMobileMenuOpen]);

    return (
        <>
            <StyledSkipLink
                className={headerCssClassNames.skipLink}
                href={`#${state.skipLinkId}`}
            >
                {state.skipLinkText}
            </StyledSkipLink>
            <StyledHeader
                className={clsx(headerCssClassNames.bar, className)}
                data-testid='header-bar'
                ref={ref}
                showMobileTitle={showMobileTitle}
            >
                <StyledContainer
                    className={headerCssClassNames.barContainer}
                    isContained={state.isContained}
                >
                    {!state.hideBranding &&
                        <StyledBrandBox className={headerCssClassNames.brandBox}>
                            {showLeftMobileMenuButton && renderMobileIcon(true)}
                            <StyledHeaderLink
                                aria-label={state.logoLinkText}
                                className={headerCssClassNames.link}
                                component={state.HeaderLogoLinkComponent}
                                href={state.logoLink}
                                {...state.HeaderLogoLinkProps}
                            >
                                <HeaderLogo/>
                            </StyledHeaderLink>
                            {!mobileBreakpoint && state.title && (
                                <>
                                    <HeaderDivider/>
                                    <StyledHeaderLink
                                        aria-label={state.title}
                                        className={headerCssClassNames.link}
                                        component={state.HeaderTitleLinkComponent}
                                        href={state.titleLink}
                                        {...state.HeaderTitleLinkProps}
                                    >
                                        <HeaderTitle/>
                                    </StyledHeaderLink>
                                </>
                            )}
                        </StyledBrandBox >
                    }
                    {state.showSearch && <HeaderSearch/>}
                    {/* Render HeaderActionArea here */}
                    {children}
                    {showRightMobileMenuButton && renderMobileIcon()}
                </StyledContainer>
            </StyledHeader>
            {showMobileTitle && <HeaderAppend/>}
            {showDesktopNavigation && <HeaderNavigation/>}
        </>
    );
});

HeaderBar.displayName = 'HeaderBar';
